import './toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_DURATION_MS = 4500;
const MAX_VISIBLE = 4;

let root: HTMLElement | null = null;

export function mountToastContainer(): void {
  if (root) return;
  const el = document.createElement('div');
  el.id = 'toast-root';
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('aria-relevant', 'additions');
  document.body.appendChild(el);
  root = el;
}

function inferType(message: string): ToastType {
  const t = message.trimStart();
  if (t.startsWith('✅')) return 'success';
  if (t.startsWith('❌')) return 'error';
  if (t.startsWith('⚠️')) return 'warning';
  return 'info';
}

export function showToast(
  message: string,
  type?: ToastType,
  durationMs = DEFAULT_DURATION_MS,
): void {
  mountToastContainer();
  if (!root) return;

  const kind = type ?? inferType(message);
  const toast = document.createElement('div');
  toast.className = `toast toast--${kind}`;
  toast.setAttribute('role', 'status');
  toast.textContent = message;

  root.appendChild(toast);

  while (root.children.length > MAX_VISIBLE) {
    root.firstElementChild?.remove();
  }

  const dismiss = () => {
    if (!toast.isConnected) return;
    toast.classList.add('toast--leaving');
    toast.addEventListener(
      'animationend',
      () => toast.remove(),
      { once: true },
    );
    setTimeout(() => toast.remove(), 300);
  };

  const timer = window.setTimeout(dismiss, durationMs);
  toast.addEventListener('click', () => {
    window.clearTimeout(timer);
    dismiss();
  });
}

export function showSuccess(message: string, durationMs?: number): void {
  showToast(message, 'success', durationMs);
}

export function showError(message: string, durationMs?: number): void {
  showToast(message, 'error', durationMs);
}

export function showWarning(message: string, durationMs?: number): void {
  showToast(message, 'warning', durationMs);
}

export function showInfo(message: string, durationMs?: number): void {
  showToast(message, 'info', durationMs);
}
